import readExcelFile from 'read-excel-file';
import {
  PricebookCategoryExtraColumns,
  PricebookGeographyTemplateBasicColumn,
  PricebookRentalColumns,
  PricebookTypeBaseColumns
} from 'constants/pricebooksTemplateColumns';
import { PricebookExtended, PricebookForHierarchy, PricebookType } from 'data/Pricebook';
import { applySortFilter } from 'utils/grid';
import { groupObjectsByField, separateWithLengthLimit } from 'utils/array';
import { sortDirection } from 'constants/sort';

const requiredFileFormat = '.xlsx';
const requiredFileSize = 20971520;

export const fileSizeToMb = (fileSize:number = 0): number => {
  return fileSize / 1024 ** 2;
};

export const pathToFileToFilename = (pathToFile: string): string => {
  return pathToFile.split('/')[1]?.split('-')[0]
};

export const errorLogUrl = (pathToFile: string) => {
  if(pathToFile)
    return (process.env.REACT_APP_MEDIA_URL || '') + '/pricebooks/' + pathToFile
  return '';
};

export const pricebookFileUrl = (pathToFile: string) => {
  if(pathToFile)
    return (process.env.REACT_APP_MEDIA_URL || '') + '/pricebooks/' + pathToFile
  return '';
};

export const filenameWithoutExtension = (filename: string = ''): string => {
  return filename.split('.')[0];
}

export const checkFileSize = (file: File | null): boolean => !!file && file.size < requiredFileSize;

export const checkFileType = (file: File | null): boolean => {
  if(!file) {
    return false
  }
  return file.name.endsWith(requiredFileFormat);
}

export const checkBrochureFileType = (file: File | null): boolean => {
  if(!file) {
    return false;
  }
  const fileName = file.name.toLowerCase();
  return fileName.endsWith('.pdf') || fileName.endsWith('.docx') || fileName.endsWith('.doc') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
}

export const verifyFileTemplate = async (
  file: File | null,
  pricebookType: string,
  pricebookCategory?: string
): Promise<boolean> => {
  if (!file) {
    return false;
  }

  const pricebooksData = await readExcelFile(file);
  const firstRow = pricebooksData[0].toString();

  const baseColumns = PricebookTypeBaseColumns[pricebookType] || PricebookGeographyTemplateBasicColumn;
  const extraColumns = (pricebookCategory && PricebookCategoryExtraColumns[pricebookCategory]) || [];
  
  const basicTemplateColumns = [...baseColumns, ...extraColumns].toString();
  const rentalTemplateColumns = [...baseColumns, ...PricebookRentalColumns, ...extraColumns].toString();

  return basicTemplateColumns === firstRow || rentalTemplateColumns === firstRow;
};


export const pricebookHierarchySort = (pricebooks: PricebookExtended[], sortType: string) => {
  applySortFilter(pricebooks, {defaultSort: sortType, field: 'vendor.name'});
  const groupedPricebooks = groupObjectsByField(pricebooks, 'vendor.name') as PricebookExtended[][];
  const mappedPricebooks = groupedPricebooks.map((group)=>{
    return pricebookHierarchySortForOneVendor(group, sortType);
  });

  return mappedPricebooks.flat(1);
}

const pricebookHierarchySortForOneVendor = (pricebooks: PricebookExtended[], sortType: string) => {
  const groupedPricebooks = groupObjectsByField(pricebooks, 'country.countryName', sortDirection.DESC) as PricebookExtended[][];

  groupedPricebooks.forEach((group)=>{
    applySortFilter(group, {defaultSort: sortType, field: 'priceBookType'});
  })
  return groupedPricebooks.flat(1);
}

export const calculatePagesForHierarchy = (pricebooks: PricebookExtended[], itemsPerPage: number) => {
  const pages = dividePricebookIntoPagesByGroups(pricebooks, itemsPerPage);
  return pages.length;
};

export const getPricebooksForPageHierarchy = (pricebooks: PricebookExtended[], itemsPerPage: number, currentPage: number): PricebookExtended[] => {
  const pages = dividePricebookIntoPagesByGroups(pricebooks, itemsPerPage);
  return pages[currentPage - 1];
}

const dividePricebookIntoPagesByGroups = (pricebooks: PricebookExtended[], itemsPerPage: number) => {
  const pricebooksGroupedByVendor = groupObjectsByField(pricebooks, 'vendor.name', sortDirection.ASC, itemsPerPage) as PricebookExtended[][];
  const mappedGroups = pricebooksGroupedByVendor.map(group=>mapVendorVisibilityForHierarchy(group));
  if(mappedGroups.length === 1) {
    return separateWithLengthLimit(mappedGroups[0], itemsPerPage) as PricebookForHierarchy[][]
  }
  const pages: PricebookExtended[][] = [];
  let currentPage: PricebookExtended[] = [];

  mappedGroups.forEach((currentGroup)=>{
    if(currentPage.length + currentGroup.length <= itemsPerPage) {
      currentPage.push(...currentGroup);
      return;
    }
    pages.push(currentPage);
    currentPage = [];
    if(currentGroup.length >= itemsPerPage) {
      pages.push(currentGroup);
    } else {
      currentPage.push(...currentGroup);
    }
  })

  if(currentPage.length) {
    pages.push(currentPage);
  }
  return pages;
}

const mapVendorVisibilityForHierarchy = (pricebookGroup: PricebookExtended[]): PricebookForHierarchy[] => {
  // @ts-ignore
  const groupedByCountry = groupObjectsByField(pricebookGroup, 'country.countryName') as PricebookExtended[][];

  const referencePricebooksCountries = new Set(
    pricebookGroup
      .filter(pricebook => pricebook.priceBookType === PricebookType.REFERENCE)
      .map(pricebook => pricebook.country?.countryName)
      .filter(countryName => !!countryName)
  );

  const sortedGroups = groupedByCountry.map((countryGroup) => {
    countryGroup.sort((a, b) => (a.priceBookType === PricebookType.REFERENCE ? -1 : 1));
    return countryGroup;
  });

  const mappedGroup: PricebookForHierarchy[] = sortedGroups.flat().map((pricebook) => {
    const isLowLevel =
      pricebook.priceBookType !== PricebookType.REFERENCE &&
      !!pricebook.country?.countryName &&
      referencePricebooksCountries.has(pricebook.country.countryName);

    return {
      ...pricebook,
      lowLevelHierarchy: isLowLevel,
    };
  });

  if (mappedGroup.length > 0) {
    mappedGroup[0].isTheFirstRowForVendor = true;
  }

  return mappedGroup;
};
